import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { isValidEmail } from '../../utils/authValidation';
import { API_BASE } from '../../config/api';
import { Eyebrow } from '../Layout/PageLayout';
import {
  AuthPage,
  AuthLayout,
  AuthIntro,
  IntroList,
  AuthCard,
  CardBody,
  CardFoot,
  PanelTitle,
  PanelText,
  Form,
  Field,
  FieldHead,
  Input,
  MessageBox,
  PrimaryButton,
  GoogleButton,
  Divider,
  InlineLink,
  googleStartUrl,
} from './AuthSharedStyles';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const { clearCart, addToCart } = useCart();
  const handledGoogleLoginRef = useRef(false);

  useEffect(() => {
    const token = searchParams.get('token');
    const nextEmail = searchParams.get('email');
    const isGoogleLogin = searchParams.get('google') === '1';
    if (!token || !nextEmail || !isGoogleLogin || handledGoogleLoginRef.current) return;
    handledGoogleLoginRef.current = true;

    try {
      login(nextEmail, token);
      window.history.replaceState({}, document.title, '/dashboard');
      navigate('/dashboard', { replace: true });
    } catch {
      handledGoogleLoginRef.current = false;
      setError('Google login completed, but finishing the session failed. Please refresh.');
    }
  }, [searchParams, login, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!isValidEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      login(email.trim(), data.token);

      const pendingCart = localStorage.getItem('pendingCart');
      if (pendingCart) {
        clearCart();
        const cartItems = JSON.parse(pendingCart);
        cartItems.forEach((item) => addToCart(item));
        localStorage.removeItem('pendingCart');
      }

      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Failed to login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPage>
      <AuthLayout>
        <AuthIntro>
          <Eyebrow>Account</Eyebrow>
          <h1>Welcome back.</h1>
          <p>Log in to see your purchases and download your files.</p>
          <IntroList>
            <li><span>01</span>All your purchases in one place</li>
            <li><span>02</span>Your cart is saved while you log in</li>
          </IntroList>
        </AuthIntro>

        <AuthCard>
          <CardBody>
            <PanelTitle>Log in</PanelTitle>
            <PanelText>Use your Google account or your email address.</PanelText>

            {error && <MessageBox $error role="alert">{error}</MessageBox>}

            <GoogleButton href={googleStartUrl()}>
              <span aria-hidden="true">G</span>
              Continue with Google
            </GoogleButton>
            <Divider>or with email</Divider>

            <Form onSubmit={handleSubmit}>
              <Field>
                <label htmlFor="login-email">Email</label>
                <Input
                  id="login-email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
              </Field>
              <Field>
                <FieldHead>
                  <label htmlFor="login-password">Password</label>
                  <InlineLink to="/forgot-password" style={{ textDecoration: 'none' }}>
                    Forgot password?
                  </InlineLink>
                </FieldHead>
                <Input
                  id="login-password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </Field>
              <PrimaryButton type="submit" disabled={loading}>
                {loading ? 'Signing in…' : 'Sign in'}
              </PrimaryButton>
            </Form>
          </CardBody>
          <CardFoot>
            No account yet? <InlineLink to="/register">Create one</InlineLink>
          </CardFoot>
        </AuthCard>
      </AuthLayout>
    </AuthPage>
  );
};

export default Login;
